/**
 * Sesli geri bildirim.
 *
 * Operatör tarama sırasında ekrana bakmaz — asıl arayüz sestir. Bu yüzden
 * her durumun kendine ait, birbirinden kolayca ayırt edilebilen bir sesi var.
 *
 * Sesler ses dosyasından değil, AudioContext ile anlık üretiliyor: dosya
 * yükleme gecikmesi yok, ilk okumada bile anında çalıyor. Eski projede
 * `just_audio` ile mp3 çalınıyor ve her çalıştan sonra `load()` çağrılıyordu
 * (rabiekle.dart:550).
 */

let ctx: AudioContext | null = null;

function getContext(): AudioContext | null {
  if (typeof AudioContext === 'undefined') return null;
  ctx ??= new AudioContext();
  // Tarayıcı otomatik oynatmayı askıya almış olabilir.
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

/** Kullanıcı etkileşiminden sonra çağrılır — tarayıcı politikası gereği. */
export function unlockAudio(): void {
  getContext();
}

interface Tone {
  freq: number;
  duration: number;
  delay: number;
  type?: OscillatorType;
  gain?: number;
}

function play(tones: Tone[]): void {
  const audio = getContext();
  if (!audio) return;

  const now = audio.currentTime;

  for (const t of tones) {
    const osc = audio.createOscillator();
    const gain = audio.createGain();

    osc.type = t.type ?? 'square';
    osc.frequency.value = t.freq;

    const start = now + t.delay;
    const end = start + t.duration;
    const peak = t.gain ?? 0.18;

    // Ani kesme "tık" sesi yapar; kısa rampa ile yumuşatılıyor.
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(peak, start + 0.008);
    gain.gain.setValueAtTime(peak, end - 0.012);
    gain.gain.linearRampToValueAtTime(0, end);

    osc.connect(gain).connect(audio.destination);
    osc.start(start);
    osc.stop(end + 0.01);
  }
}

/** İlk barkod okundu — kısa, nötr. "Devam et." */
export function sesIlkOkuma(): void {
  play([{ freq: 880, duration: 0.06, delay: 0 }]);
}

/** Eşleştirme tamamlandı — yükselen ikili, tatmin edici. "Bitti, sıradaki." */
export function sesEslestirmeTamam(): void {
  play([
    { freq: 880, duration: 0.055, delay: 0 },
    { freq: 1320, duration: 0.09, delay: 0.055 },
  ]);
}

/** Uyarı: bu kod zaten kullanılmış — alçak, dikkat çekici. */
export function sesUyari(): void {
  play([
    { freq: 320, duration: 0.11, delay: 0, type: 'sawtooth', gain: 0.16 },
    { freq: 260, duration: 0.16, delay: 0.13, type: 'sawtooth', gain: 0.16 },
  ]);
}

/** Hata: kayıt reddedildi — belirgin şekilde farklı. */
export function sesHata(): void {
  play([
    { freq: 200, duration: 0.22, delay: 0, type: 'sawtooth', gain: 0.2 },
  ]);
}
